/**
 * Suppresses the on-screen keyboard when PowerDuck dropdowns open on touch
 * devices. Two focus paths pop the keyboard without user intent:
 *  - inline panel (viewports > 991px, e.g. iPad landscape / 12.9" portrait):
 *    dropdown/index.tsx calls focusSearch() programmatically on open
 *  - modal panel: the opener tap's ghost click can land on the search input
 * Both cases are preceded by a pointerdown that did NOT target the search
 * input — that is the suppression key. The pointer TYPE of that pointerdown
 * is the touch gate: only touch/pen interactions pop an on-screen keyboard,
 * so mouse/trackpad clicks never suppress. Per-event pointerType sidesteps
 * both media-query mismatches: iPadOS reports pointer: coarse even with a
 * Magic Keyboard attached (a trackpad click must keep focus so typing
 * filters), and Windows touch laptops report pointer: fine while PowerDuck
 * still classifies them as mobile (a finger tap must still suppress).
 * A focus with NO recent pointerdown is keyboard-driven (Enter/ArrowDown/
 * Tab) and is always kept: PowerDuck routes all open-panel keyboard
 * navigation (arrows, typing, Enter, Escape) through this input, so
 * blurring it would make dropdowns keyboard-inoperable (WCAG 2.1.1).
 */
export function installDropdownKeyboardGuard(): void {
	let lastPointerDownTarget: EventTarget | null = null;
	let lastPointerDownType = '';
	let lastPointerDownAt = 0;

	document.addEventListener('pointerdown', (e) => {
		lastPointerDownTarget = e.target;
		lastPointerDownType = e.pointerType;
		lastPointerDownAt = Date.now();
	}, true);

	document.addEventListener('focusin', (e) => {
		const target = e.target as HTMLElement;
		if (!(target instanceof HTMLElement) || !target.classList.contains('pd-dd-search-input')) {
			return;
		}

		// No pointer activity in the last second — keyboard-initiated focus
		// (also covers a slow long-press-to-paste on the field itself, whose
		// focus can arrive after the window expires). Keep it.
		if (Date.now() - lastPointerDownAt >= 1000) {
			return;
		}

		// Mouse/trackpad interaction — no on-screen keyboard can pop; keep
		// the focus so click-then-type keeps working (desktops, hybrids,
		// iPad trackpads).
		if (lastPointerDownType === 'mouse') {
			return;
		}

		// The recent tap landed on the search input itself — user intent.
		if (target === lastPointerDownTarget) {
			return;
		}

		// Recent touch/pen tap elsewhere: this focus is the programmatic
		// focusSearch() or a synthesized ghost click — suppress the keyboard.
		target.blur();
	}, true);
}
